import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ChannelGroup, ChannelMeta } from "../enrichment-list.channels";
import { EnrichmentChannelDirectory } from "../../../../services/enrichment-channel-directory.service";

/**
 * Channel picker shown in the "pick a channel before edit" modal.
 *
 * It is a standalone component (not inline modal markup) on purpose: the
 * omniark modal projects content into an overlay outside the list
 * component's encapsulation scope, so list-component styles never reach it.
 * Component-scoped styles travel with the component instance wherever it is
 * rendered, so the styling here always applies — no global CSS needed.
 */
@Component({
  selector: "app-enrichment-channel-gate",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./enrichment-channel-gate.html",
  styleUrls: ["./enrichment-channel-gate.scss"],
})
export class EnrichmentChannelGate {
  private readonly channelDirectory = inject(EnrichmentChannelDirectory);

  /** Currently-selected channelKey (or "" when none picked). */
  @Input() selected: string | "" = "";
  /** Sales channels of the package being edited. The picker is scoped to these
   *  so the user can only edit channels the package actually sells on. The
   *  "All" wildcard shows every channel, even over {@link allowedKeys}; an
   *  empty list shows none (the package sells nowhere, so there is nothing to
   *  edit). */
  @Input() salesChannels: string[] = [];
  /** Channel keys the backend says the package can be edited under (GET
   *  …/editable-channels). Null = not resolved (request pending or failed):
   *  fall back to the local `salesChannels` rule. Resolved, it narrows a
   *  specific sales-channel list to the listed keys. It never narrows the
   *  "All" wildcard: the backend applies the `enableChannelRuleAll` gate
   *  there, and a package sold on All must stay editable on every channel. */
  @Input() allowedKeys: readonly string[] | null = null;
  /** True while the backend is being asked; the list shows placeholder rows
   *  instead of options or the empty state. */
  @Input() loading = false;
  @Output() pick = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  search = "";

  constructor() {
    // The list page kicks the first fetch; this is a safety net for the
    // case the gate is the first thing to render (e.g. deep-link / refresh).
    this.channelDirectory.prime();
  }

  /** The package sells on the "All" wildcard, so every channel is offered
   *  without waiting on the backend. */
  get sellsEverywhere(): boolean {
    return (this.salesChannels ?? []).some((c) => lower(c).trim() === "all");
  }

  get groupedChannels(): { group: ChannelGroup; items: ChannelMeta[] }[] {
    const q = this.search.trim().toLowerCase();
    const scoped = this.scopedChannels();
    const matches = q
      ? scoped.filter(
          (m) => lower(m.label).includes(q) || lower(m.value).includes(q),
        )
      : scoped;
    const groups: ChannelGroup[] = [];
    for (const m of matches) {
      if (!groups.includes(m.group)) groups.push(m.group);
    }
    return groups.map((group) => ({
      group,
      items: matches.filter((m) => m.group === group),
    }));
  }

  /** Channels the package may be edited under. The "All" wildcard offers every
   *  channel; otherwise the backend answer wins when resolved; an empty
   *  sales-channel list offers none (nothing to edit); a specific list is
   *  scoped to those channels (matched by key, name, or label,
   *  case-insensitive). */
  private scopedChannels(): ChannelMeta[] {
    // A master row without a channelKey cannot be picked: the key is what
    // becomes `x-channel`. Live masters do carry such rows (SIT channel 28,
    // "MC3.0"), and matching on their null key threw inside this getter,
    // which left the whole list blank — not even the empty state rendered.
    const all = this.channelDirectory.channels().filter((m) => !!m.value);
    if (this.sellsEverywhere) return all;
    if (this.allowedKeys !== null) {
      const allowed = new Set(this.allowedKeys.map((k) => lower(k).trim()));
      return all.filter((m) => allowed.has(lower(m.value)));
    }
    const sales = this.salesChannels ?? [];
    // No sales channels → nothing to edit; the picker shows an empty state.
    if (sales.length === 0) return [];
    const allowed = new Set(sales.map((c) => lower(c).trim()));
    return all.filter(
      (m) =>
        allowed.has(lower(m.value)) ||
        allowed.has(lower(m.channelName)) ||
        allowed.has(lower(m.label)),
    );
  }

  onPick(value: string): void {
    this.pick.emit(value);
  }
}

/** Lower-cases a master-data string that is typed `string` but can arrive
 *  null from the API. */
function lower(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}
